/*
 * Layout for the living Nono map (SkyMap.tsx). Pure functions, no React.
 *
 * Every stage becomes a small tree — a "constellation". Unlike SkillTree's
 * decorative chains, a branch here IS a dependency: a job hangs under the
 * in-stage job it needs (the deepest one when it needs several). Jobs that
 * only need things from other stages (or nothing) hang from the stage root.
 * Cross-stage dependencies are drawn on demand as arcs.
 */

import { JOBS, type Job, type Level, STAGES, type Stage } from './nonoMap';

export type Pt = { x: number; y: number };

export const JOB_BY_ID = new Map(JOBS.map((j) => [j.id, j]));
export const STAGE_BY_ID = new Map(STAGES.map((s) => [s.id, s]));
export const STAGE_INDEX = new Map(STAGES.map((s, i) => [s.id, i]));

export const DEPENDENTS = new Map<string, string[]>();
for (const j of JOBS) {
  for (const d of j.deps) DEPENDENTS.set(d, [...(DEPENDENTS.get(d) ?? []), j.id]);
}

/** Every job this one needs, directly or through others (all stages). */
export function ancestors(id: string): Set<string> {
  const out = new Set<string>();
  const walk = (j: string) => {
    for (const d of JOB_BY_ID.get(j)?.deps ?? []) {
      if (!out.has(d)) {
        out.add(d);
        walk(d);
      }
    }
  };
  walk(id);
  return out;
}

export const LEVEL_RANK: Record<Level, number> = { auto: 0, assisted: 1, manual: 2, missing: 3 };

/* ── per-stage identity (colour + icon key), like SkillTree's departments ── */

export const STAGE_COLOR: Record<string, string> = {
  plan: '#FF9D5C',
  lage: '#A78BFA',
  sjekke: '#FB7185',
  godkjenne: '#FACC15',
  publisere: '#5EEAD4',
  laere: '#7DD3FC',
};

/** Short sky labels for narrow screens, where the full names would collide. */
export const STAGE_SHORT: Record<string, Record<'nb' | 'en', string>> = {
  plan: { nb: 'Plan', en: 'Plan' },
  lage: { nb: 'Lage', en: 'Make' },
  sjekke: { nb: 'Sjekk', en: 'Check' },
  godkjenne: { nb: 'Godkjenn', en: 'Approve' },
  publisere: { nb: 'Publiser', en: 'Publish' },
  laere: { nb: 'Lære', en: 'Learn' },
};

/* ── trees ───────────────────────────────────────────────────────────── */

export interface TreeNode {
  id: string;
  job: Job;
  parent: string | null;
  children: string[];
  depth: number; // 1 = hangs from the stage root
  leaves: number;
}

export interface StageTree {
  stage: Stage;
  index: number;
  roots: string[];
  nodes: Map<string, TreeNode>;
  maxDepth: number;
  leaves: number;
  counts: Record<Level, number>;
}

function buildTree(stage: Stage, index: number): StageTree {
  const jobs = JOBS.filter((j) => j.stage === stage.id);
  const inStage = new Set(jobs.map((j) => j.id));
  const depth = new Map<string, number>();
  const parent = new Map<string, string | null>();

  const resolve = (id: string, seen: Set<string>): number => {
    const known = depth.get(id);
    if (known !== undefined) return known;
    if (seen.has(id)) return 1; // cycle guard — data has none, but never loop
    seen.add(id);
    let best: string | null = null;
    let bestDepth = 0;
    for (const d of JOB_BY_ID.get(id)?.deps ?? []) {
      if (!inStage.has(d)) continue;
      const dd = resolve(d, seen);
      if (dd > bestDepth) {
        bestDepth = dd;
        best = d;
      }
    }
    parent.set(id, best);
    depth.set(id, bestDepth + 1);
    return bestDepth + 1;
  };
  for (const j of jobs) resolve(j.id, new Set());

  const byRank = (a: string, b: string) =>
    LEVEL_RANK[JOB_BY_ID.get(a)?.level ?? 'auto'] - LEVEL_RANK[JOB_BY_ID.get(b)?.level ?? 'auto'];
  const nodes = new Map<string, TreeNode>();
  for (const j of jobs) {
    nodes.set(j.id, {
      id: j.id,
      job: j,
      parent: parent.get(j.id) ?? null,
      children: [],
      depth: depth.get(j.id) ?? 1,
      leaves: 1,
    });
  }
  for (const n of nodes.values()) if (n.parent) nodes.get(n.parent)?.children.push(n.id);
  for (const n of nodes.values()) n.children.sort(byRank);
  const roots = [...nodes.values()]
    .filter((n) => !n.parent)
    .map((n) => n.id)
    .sort(byRank);

  const countLeaves = (id: string): number => {
    const n = nodes.get(id);
    if (!n) return 0;
    n.leaves = n.children.length ? n.children.reduce((s, c) => s + countLeaves(c), 0) : 1;
    return n.leaves;
  };
  const leaves = roots.reduce((s, r) => s + countLeaves(r), 0);

  const counts: Record<Level, number> = { auto: 0, assisted: 0, manual: 0, missing: 0 };
  for (const j of jobs) counts[j.level]++;

  return {
    stage,
    index,
    roots,
    nodes,
    maxDepth: Math.max(1, ...[...nodes.values()].map((n) => n.depth)),
    leaves,
    counts,
  };
}

export const TREES: StageTree[] = STAGES.map(buildTree);
export const TREE_BY_STAGE = new Map(TREES.map((t) => [t.stage.id, t]));

/* ── geometry helpers ────────────────────────────────────────────────── */

/** 0° points up, angles run clockwise — like a clock face. */
export const polar = (r: number, deg: number): Pt => {
  const a = (deg * Math.PI) / 180;
  return { x: r * Math.sin(a), y: -r * Math.cos(a) };
};

/** Rotate a local point (local "up" = outward) by `deg`, then move it to `origin`. */
export const place = (p: Pt, deg: number, origin: Pt): Pt => {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: origin.x + p.x * c - p.y * s, y: origin.y + p.x * s + p.y * c };
};

export const lerp = (a: number, b: number, e: number) => a + (b - a) * e;
export const lerpPt = (a: Pt, b: Pt, e: number): Pt => ({
  x: lerp(a.x, b.x, e),
  y: lerp(a.y, b.y, e),
});
export const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
/** Shortest signed angle from a to b, in degrees (−180..180]. */
export const angleDelta = (a: number, b: number) => ((((b - a) % 360) + 540) % 360) - 180;

/** Deterministic 0..1 from a string — the "organic" jitter is the same on every load. */
export function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

/** Seeded PRNG for the stars and the brain. */
export function rng(seed: number) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Lay a stage tree out in its own local frame: the root at (0,0), branches
 * fanning "up" (−y) inside `span` degrees; each subtree gets an angle share
 * proportional to its leaves, and the radius grows with depth.
 */
export function layoutTree(
  tree: StageTree,
  span: number,
  radius: (depth: number) => number,
  jitter: { deg: number; r: number },
): Map<string, Pt> {
  const out = new Map<string, Pt>();
  const lay = (ids: string[], a0: number, a1: number) => {
    const total = ids.reduce((s, id) => s + (tree.nodes.get(id)?.leaves ?? 1), 0) || 1;
    let cur = a0;
    for (const id of ids) {
      const n = tree.nodes.get(id);
      if (!n) continue;
      const share = ((a1 - a0) * n.leaves) / total;
      const h = hash01(id);
      const a = cur + share / 2 + (h - 0.5) * 2 * Math.min(jitter.deg, share * 0.3);
      const r = radius(n.depth) + (hash01(`${id}r`) - 0.5) * 2 * jitter.r;
      out.set(id, polar(r, a));
      lay(n.children, cur, cur + share);
      cur += share;
    }
  };
  lay(tree.roots, -span / 2, span / 2);
  return out;
}

/* ── the sky (overview) ─────────────────────────────────────────────── */

export const SKY = {
  ring: 228, // stage roots sit on this circle
  labelGap: 70, // stage name sits this far beyond its own constellation
  brain: 100, // particle cloud radius
  first: 74, // first ring of jobs from the stage root
  reach: 190, // deepest ring sits at most this much further out
  jitter: { deg: 6, r: 6 },
  step: 360 / STAGES.length,
  /** Line layout: two rows of five, roots at the bottom of each cell. */
  cols: 5,
  colW: 330,
  rowH: 470,
} as const;

/** A thin chain gets a narrow fan, a bushy stage a wide one — like real constellations. */
export const skySpan = (t: StageTree) => Math.min(108, Math.max(46, 18 + t.leaves * 17));
const skyRadius = (t: StageTree) => {
  const step = t.maxDepth > 1 ? Math.min(56, SKY.reach / (t.maxDepth - 1)) : 56;
  return (d: number) => SKY.first + (d - 1) * step;
};

/** Local (unrotated) positions of each stage's jobs in the sky. */
export const SKY_LOCAL = new Map(
  TREES.map((t) => [t.stage.id, layoutTree(t, skySpan(t), skyRadius(t), SKY.jitter)]),
);

/** How far each constellation reaches from its root — its label sits just beyond. */
export const SKY_REACH = new Map(
  TREES.map((t) => {
    const pts = [...(SKY_LOCAL.get(t.stage.id)?.values() ?? [])];
    return [t.stage.id, Math.max(SKY.first, ...pts.map((p) => -p.y))];
  }),
);
export const SKY_MAX_LABEL = SKY.ring + Math.max(...SKY_REACH.values()) + SKY.labelGap;

/** Where a stage's root sits in the line layout (rotation 0 there). */
export function lineRoot(index: number): Pt {
  const col = index % SKY.cols;
  const row = Math.floor(index / SKY.cols);
  return {
    x: (col - (SKY.cols - 1) / 2) * SKY.colW,
    y: (row - 0.5) * SKY.rowH + 120,
  };
}

/* ── the dive (one stage, large) ─────────────────────────────────────── */

export const FAN = {
  span: 176,
  first: 230,
  maxReach: 560, // keep deep stages (booking) from growing without bound
  node: 30, // node radius
  root: 46,
};

export function fanLayout(tree: StageTree): Map<string, Pt> {
  const step = tree.maxDepth > 1 ? Math.min(170, FAN.maxReach / (tree.maxDepth - 1)) : 170;
  return layoutTree(tree, FAN.span, (d) => FAN.first + (d - 1) * step, { deg: 2.5, r: 6 });
}

export const FAN_LOCAL = new Map(TREES.map((t) => [t.stage.id, fanLayout(t)]));

/** Bounds of a stage's fan, incl. labels under nodes and the name under the root. */
export function fanBounds(stageId: string) {
  const pts = [...(FAN_LOCAL.get(stageId)?.values() ?? [])];
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const pad = 110;
  return {
    x0: Math.min(-160, ...xs) - pad,
    x1: Math.max(160, ...xs) + pad,
    y0: Math.min(-120, ...ys) - 90,
    y1: 175, // root (0,0) + stage name and sub-line underneath
  };
}
